/** Master switch — must be the string `'true'` to enable GA when an ID is set. */
export const ANALYTICS_ACTIVE_ENV = 'NEXT_PUBLIC_ANALYTICS_ACTIVE' as const;

/** GA4 measurement ID (`G-…`). */
export const GA_MEASUREMENT_ID_ENV = 'NEXT_PUBLIC_GA_MEASUREMENT_ID' as const;

export const ANALYTICS_ACTIVE_VALUE = 'true' as const;

export const ANALYTICS_MISSING_ID_WARNING =
  'Analytics is active but NEXT_PUBLIC_GA_MEASUREMENT_ID is missing' as const;

/** Google’s programmatic opt-out window flag prefix (`ga-disable-G-…`). */
export const GA_DISABLE_PREFIX = 'ga-disable-' as const;

/** Client ID cookie set by GA4 after consent. */
export const GA_CLIENT_ID_COOKIE = '_ga' as const;

/** Session cookie prefix; full name is `_ga_<measurementIdWithoutGPrefix>`. */
export const GA_SESSION_COOKIE_PREFIX = '_ga_' as const;

/** Custom GA4 event names (Notion taxonomy). */
export const GA_EVENTS = {
  sectionView: 'section_view',
  navClick: 'nav_click',
  chartFilter: 'chart_filter',
  chordSelect: 'chord_select',
  pressureCardToggle: 'pressure_card_toggle',
  overlayOpen: 'overlay_open',
  overlayNavigate: 'overlay_navigate',
  overlayClose: 'overlay_close',
  audioPlay: 'audio_play',
  ctaClick: 'cta_click',
  outboundClick: 'outbound_click',
  languageSwitch: 'language_switch',
  cookieConsent: 'cookie_consent',
} as const;

export type GaEventName = (typeof GA_EVENTS)[keyof typeof GA_EVENTS];

/**
 * Interaction events used by the GA4 Admin “Response engaged” audience
 * (composite key event `response_engaged`).
 */
export const GA_INTERACTION_EVENTS = [
  GA_EVENTS.chartFilter,
  GA_EVENTS.chordSelect,
  GA_EVENTS.pressureCardToggle,
  GA_EVENTS.overlayOpen,
  GA_EVENTS.audioPlay,
] as const;

/** Event parameter keys (register as custom dimensions in GA4 Admin). */
export const GA_PARAMS = {
  chapterId: 'chapter_id',
  chartId: 'chart_id',
  flywayId: 'flyway_id',
  viewMode: 'view_mode',
  overlaySet: 'overlay_set',
  overlayEntryId: 'overlay_entry_id',
  ctaId: 'cta_id',
  placement: 'placement',
  linkPlacement: 'link_placement',
  linkUrl: 'link_url',
  linkDomain: 'link_domain',
  audioId: 'audio_id',
  pressureCardId: 'pressure_card_id',
  method: 'method',
  locale: 'locale',
  selectionType: 'selection_type',
  countryId: 'country_id',
  expanded: 'expanded',
  choice: 'choice',
  source: 'source',
  fromLocale: 'from_locale',
  toLocale: 'to_locale',
} as const;

export const GA_CHAPTER_ID = {
  hero: 'hero',
  introduction: 'introduction',
  state: 'state',
  pressure: 'pressure',
  response: 'response',
  conclusion: 'conclusion',
} as const;

export type GaChapterId = (typeof GA_CHAPTER_ID)[keyof typeof GA_CHAPTER_ID];

/** Section element ids observed by `SectionViewTracker`. */
export const GA_SECTION_VIEW_IDS = [
  GA_CHAPTER_ID.hero,
  GA_CHAPTER_ID.introduction,
  GA_CHAPTER_ID.state,
  GA_CHAPTER_ID.pressure,
  GA_CHAPTER_ID.response,
  GA_CHAPTER_ID.conclusion,
] as const;

export const GA_SECTION_VIEW_THRESHOLD = 0.5;

export const GA_CHART_ID = {
  flywaysMap: 'flyways_map',
  chord: 'chord',
  populationTrends: 'population_trends',
  redListCategories: 'red_list_categories',
  redListIndex: 'red_list_index',
} as const;

export type GaChartId = (typeof GA_CHART_ID)[keyof typeof GA_CHART_ID];

export const GA_CTA_ID = {
  support: 'support',
  downloadReport: 'download_report',
} as const;

export type GaCtaId = (typeof GA_CTA_ID)[keyof typeof GA_CTA_ID];

export const GA_CTA_PLACEMENT = {
  navbar: 'navbar',
  conclusion: 'conclusion',
  mobileMenu: 'mobile_menu',
} as const;

export type GaCtaPlacement = (typeof GA_CTA_PLACEMENT)[keyof typeof GA_CTA_PLACEMENT];

export const GA_LINK_PLACEMENT = {
  datazoneNav: 'datazone_nav',
  datazoneChart: 'datazone_chart',
  species: 'species',
  pressureArticle: 'pressure_article',
  footer: 'footer',
  social: 'social',
} as const;

export type GaLinkPlacement =
  (typeof GA_LINK_PLACEMENT)[keyof typeof GA_LINK_PLACEMENT];

export const GA_OVERLAY_SET = {
  actions: 'actions',
  enablers: 'enablers',
} as const;

export type GaOverlaySet = (typeof GA_OVERLAY_SET)[keyof typeof GA_OVERLAY_SET];

/** Chapter nav `method` for `nav_click`. `mobile_menu` extends Notion (sticky_nav only). */
export const GA_NAV_METHOD = {
  stickyNav: 'sticky_nav',
  mobileMenu: 'mobile_menu',
} as const;

export type GaNavMethod = (typeof GA_NAV_METHOD)[keyof typeof GA_NAV_METHOD];

export const GA_OVERLAY_NAV_METHOD = {
  control: 'control',
  swipe: 'swipe',
  keyboard: 'keyboard',
} as const;

export type GaOverlayNavMethod =
  (typeof GA_OVERLAY_NAV_METHOD)[keyof typeof GA_OVERLAY_NAV_METHOD];

export const GA_OVERLAY_CLOSE_METHOD = {
  button: 'button',
  backdrop: 'backdrop',
  escape: 'escape',
} as const;

export type GaOverlayCloseMethod =
  (typeof GA_OVERLAY_CLOSE_METHOD)[keyof typeof GA_OVERLAY_CLOSE_METHOD];

/** Text values for GA4 custom dimensions registered as text (not boolean). */
export const GA_EXPANDED = {
  true: 'true',
  false: 'false',
} as const;

export type GaExpanded = (typeof GA_EXPANDED)[keyof typeof GA_EXPANDED];

export const GA_CHORD_SELECTION_TYPE = {
  country: 'country',
  ribbon: 'ribbon',
} as const;

export type GaChordSelectionType =
  (typeof GA_CHORD_SELECTION_TYPE)[keyof typeof GA_CHORD_SELECTION_TYPE];

export const GA_CONSENT_CHOICE = {
  granted: 'granted',
  denied: 'denied',
} as const;

export type GaConsentChoice =
  (typeof GA_CONSENT_CHOICE)[keyof typeof GA_CONSENT_CHOICE];

export const GA_CONSENT_SOURCE = {
  banner: 'banner',
  settings: 'settings',
} as const;

export type GaConsentSource =
  (typeof GA_CONSENT_SOURCE)[keyof typeof GA_CONSENT_SOURCE];

export const GA_AUDIO_ID = {
  arcticTern: 'arctic_tern',
  bobolink: 'bobolink',
  spoonBilledSandpiper: 'spoon_billed_sandpiper',
} as const;

export type GaAudioId = (typeof GA_AUDIO_ID)[keyof typeof GA_AUDIO_ID];

export const GA_CHORD_VIEW_MODE = {
  breeding: 'breeding',
  nonBreeding: 'nonBreeding',
} as const;

export const GA_STATE_VIEW_MODE = {
  default: 'default',
  speciesGroup: 'speciesGroup',
  habitat: 'habitat',
} as const;
